import React, { useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';

import { THead, Th } from '@/components/ui/table-kit';
import { useOldTagPages, useTrackingDomains } from '@/hooks/use-tracking-domains';
import { fmtZoned } from '@/lib/utils';

/**
 * Pages that still load a tag from Cookiebees' own domain, after the workspace
 * moved to its own.
 *
 * The case this exists for is silent by nature: a second theme layout, a
 * landing page builder, a GTM container nobody updated. Each keeps sending,
 * third-party, and nothing on the site looks wrong - the dashboard shows the
 * workspace as first-party while a share of its visitors are not. Read from
 * the per-request transport ledger (api services/transportLedger): what
 * actually arrived on our domain in the last 7 days, not a scan of the site.
 *
 * Shown on the tracking domain setup card and in the header chip's panel, and
 * only once the workspace's own transport works: before that, every page
 * loading from our domain is expected, not a leftover.
 */

export const SCRIPT_LABEL = {
  lead: 'Lead tag',
  store: 'Store tag',
  wa_widget: 'WhatsApp widget',
  chat: 'Chat widget',
  popups: 'Popups',
  image: 'Image pixel',
};

function when(ts) {
  if (!ts) return '';
  return fmtZoned(ts, 'd MMM, h:mm aaa');
}

/**
 * Pages to fix, or 0 when what is left is only pages cached from before the
 * switch (api transportLedger.leftoverVerdict: the last two days' share of
 * loads). Null while it loads.
 */
export function useOldTagCount(enabled) {
  const { data } = useOldTagPages(enabled);
  if (!data) return null;
  return data.recent && !data.recent.needs_fix ? 0 : data.count;
}

const pctText = (share) => (share > 0 && share < 0.001 ? 'under 0.1%' : `${(share * 100).toFixed(share < 0.01 ? 2 : 1)}%`);

/** "11 of 215,101 page loads in the last 2 days (under 0.1%)". */
export function recentText(recent) {
  if (!recent || !recent.all_loads) return null;
  return `${recent.old_loads.toLocaleString('en-IN')} of ${recent.all_loads.toLocaleString('en-IN')} page loads in the last ${recent.days} days (${pctText(recent.share)})`;
}

export function OldTagPages({ enabled = true, max = 50 }) {
  const { data, isLoading, error } = useOldTagPages(enabled);
  const { data: td } = useTrackingDomains();
  const [all, setAll] = useState(false);
  const fp = td?.first_party;
  const yours = fp?.host ? (fp.kind === 'path' && fp.token ? `${fp.host}/${fp.token}` : fp.host) : null;
  if (!enabled) return null;
  if (isLoading) return <p className="text-[13px] text-muted-foreground">Checking which pages still load the old tag...</p>;
  // A failed read is said, never shown as "no pages": an empty list here is
  // the good news, so it must not also be what an error looks like.
  if (error) return <p className="text-[13px] text-rose-600 dark:text-rose-400">Could not read which pages load the old tag: {error.response?.data?.error || error.message}</p>;
  const pages = data?.pages || [];
  // `since` is a calendar day: drawn as it is (UTC midnight read a day early west of UTC).
  const since = data?.since ? new Date(`${data.since}T00:00:00`) : null;
  const recentStart = since && (Date.now() - since.getTime()) < 7 * 864e5;
  const sinceText = since ? since.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : null;

  if (!pages.length) {
    return (
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        No page loaded a tag from Cookiebees&apos; domain in the last 7 days
        {recentStart && sinceText ? <> (we have been recording this since {sinceText})</> : null}.
      </p>
    );
  }

  // Five most recent by default: the list is evidence, the fix is the point
  // (owner, 14 Oct 2026: "explain that as well - use less text").
  const shown = pages.slice(0, all ? max : 5);
  const code = (t) => <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[12.5px] text-foreground">{t}</span>;
  // Only a trickle left: pages browsers cached before the switch. Said as
  // what it is, with the pages still listed as the evidence.
  if (data.recent && !data.recent.needs_fix) {
    return (
      <div className="space-y-3">
        <p className="text-[14px] leading-relaxed">
          Nothing to change. Only {recentText(data.recent)} came from the old address: pages cached in browsers before
          the switch, which stop as they refresh.
        </p>
        <OldPageList shown={shown} pages={pages} setAll={setAll} max={max} count={data.count} />
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-[14px] leading-relaxed">
        <b className="tabular-nums">{data.count.toLocaleString('en-IN')}</b> page{data.count === 1 ? '' : 's'} still
        load{data.count === 1 ? 's' : ''} the Cookiebees tag from our domain, not yours, so Safari and ad blockers cut
        what {data.count === 1 ? 'it records' : 'they record'} (last 7 days{recentStart && sinceText ? <>, since {sinceText}</> : null}).
      </p>
      <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-[13.5px] leading-relaxed">
        <p className="font-semibold text-foreground">How to fix</p>
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-muted-foreground">
          In the tag on these pages (usually one theme or template they share), change the address
          {code('app.cookiebees.io')}<ArrowRight className="size-3.5" />{code(yours || 'your own address')}.
          The full tag is in Install the code.
        </p>
      </div>
      <OldPageList shown={shown} pages={pages} setAll={setAll} max={max} count={data.count} />
    </div>
  );
}

/** The pages, most recent first, five until asked for more. */
function OldPageList({ shown, pages, setAll, max, count }) {
  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-[14px]">
          <THead className="bg-muted/40">
            <tr>
              <Th>Page</Th>
              <Th>Script</Th>
              <Th align="right">Last seen</Th>
            </tr>
          </THead>
          <tbody>
            {shown.map((p) => (
              <tr key={`${p.script}|${p.page_host}|${p.page_path}`} className="border-t border-border/60 align-top">
                <td className="max-w-[360px] px-4 py-2">
                  <span className="break-all font-mono text-[13px]">{p.page_host || 'unknown site'}{p.page_path || ''}</span>
                  {!p.page_path && (
                    <span className="block text-[13px] text-muted-foreground">
                      Somewhere on this site: a widget loaded cross-site reports the site, not the page.
                    </span>
                  )}
                  {p.direct_fp && (
                    <span className="block text-[13px] text-muted-foreground">
                      The tag names our /fp address directly instead of the path on your site.
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2">{SCRIPT_LABEL[p.script] || p.script}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-muted-foreground">{when(p.last_seen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages.length > shown.length ? (
        <button type="button" onClick={() => setAll(true)} className="flex items-center gap-1 text-[13.5px] font-medium text-primary hover:underline">
          <ChevronDown className="size-3.5" /> Show more pages ({Math.min(max, pages.length)} most recent of {count.toLocaleString('en-IN')})
        </button>
      ) : count > shown.length ? (
        <p className="text-[13px] text-muted-foreground">Showing the {shown.length} most recent of {count.toLocaleString('en-IN')}.</p>
      ) : null}
    </>
  );
}

export default OldTagPages;
